import { useQuery } from '@apollo/client'
import {
  Chip,
  Hidden,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import Pagination from '@material-ui/lab/Pagination'
import React, { useState } from 'react'
import { toast } from 'react-toastify'

import {
  PROMOTION_CODES_QUERY,
  PromotionCodesData,
  PromotionCodesVariables
} from '../../../graphql/promotions'
import { getTranslate } from '../../../localization'
import PromotionCodesTable from './PromotionCodesTable'
import PromotionCodesTableMobile from './PromotionCodesTableMobile'
import useStyles from './styles/PromotionCodesTable.styles'

const PER_PAGE = 20

/**
 * The voucher pool's audit view (BL-145).
 *
 * Codes could be issued in bulk and never observed: loyalty's Reserve/Confirm/Release
 * have always written promotion_code.state, reserved_booking_id and consumed_at, and
 * nothing read them back. This is the read side, on the same screen that issues them —
 * issue above, audit below, one subject.
 */
const PromotionCodesSection: React.FC<{ promotionId: string }> = ({
  promotionId
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [state, setState] = useState('')
  const [page, setPage] = useState(1)

  const { data, loading, refetch } = useQuery<
    PromotionCodesData,
    PromotionCodesVariables
  >(PROMOTION_CODES_QUERY, {
    // Unfiltered on first paint, for every role — a list that requires a filter before
    // showing rows reads as broken to someone who does not yet know what to filter by
    // (conventions section 2).
    variables: {
      promotionId,
      state: state || undefined,
      page,
      perPage: PER_PAGE
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    // Section 7: a failed query toasts. NOT an empty table — on an audit surface "no
    // codes" looks like information, and would be read as an empty pool.
    onError: (err) => toast.error(err.message)
  })

  const result = data?.promotionCodes
  const codes = result?.codes || []
  const pageCount = Math.max(1, Math.ceil((result?.total || 0) / PER_PAGE))

  return (
    <Paper className={classes.paper}>
      <Typography variant="h4" gutterBottom>
        {getTranslate('Issued vouchers')}
      </Typography>

      {/*
        Pool totals, not page totals. The first question about a voucher pool is how many
        are left, and these are unaffected by the filter or the page — deriving them from
        the visible rows is exactly the arithmetic that goes wrong.
      */}
      <div className={classes.totals}>
        <Chip
          label={`${getTranslate('Available')}: ${result?.totalAvailable ?? 0}`}
        />
        <Chip
          color="primary"
          label={`${getTranslate('Reserved')}: ${result?.totalReserved ?? 0}`}
        />
        <Chip
          color="secondary"
          label={`${getTranslate('Consumed')}: ${result?.totalConsumed ?? 0}`}
        />
      </div>

      {/*
        ONE filter field, and it is an enum rather than a text search — which is neither
        documented pattern: conventions section 3 gives inline search for a single TEXT
        field and a collapsed panel for 2+. Inline is the closer fit; a panel with one
        select behind a toggle costs a click to reach a control that fits beside the
        table. Recorded as a judgement between the two rather than a rule being followed.

        Applies on change, which the panel pattern forbids — that prohibition is about
        panels re-querying as you TYPE. A select has no intermediate states.
      */}
      <TextField
        select
        size="small"
        margin="dense"
        id="promotion-code-state"
        label={getTranslate('State')}
        value={state}
        onChange={(event) => {
          setState(event.target.value)
          // Page 3 of "all" is rarely page 3 of "consumed", and silently showing an empty
          // page after a filter change reads as "no codes in that state".
          setPage(1)
        }}>
        <MenuItem value="">{getTranslate('All')}</MenuItem>
        <MenuItem value="available">{getTranslate('available')}</MenuItem>
        <MenuItem value="reserved">{getTranslate('reserved')}</MenuItem>
        <MenuItem value="consumed">{getTranslate('consumed')}</MenuItem>
      </TextField>

      {/* Separate desktop and mobile components, duplicating LAYOUT and never behaviour —
          both render the same rows from the same query above (section 2). */}
      <Hidden smDown>
        <PromotionCodesTable
          data={codes}
          loading={loading}
          reloadData={() => refetch()}
        />
      </Hidden>
      <Hidden mdUp>
        <PromotionCodesTableMobile
          data={codes}
          loading={loading}
          reloadData={() => refetch()}
        />
      </Hidden>

      <div className={classes.paginationContainer}>
        <Pagination
          count={pageCount}
          color="primary"
          size={isSmallScreen ? 'small' : 'medium'}
          page={page}
          onChange={(_, value) => setPage(value)}
        />
      </div>
    </Paper>
  )
}

export default PromotionCodesSection
